import { fetchReportHTML, stopPolling, incrementPollingAttempts } from '../redux/slices/aiReportsSlice';

// Polling configuration
const INITIAL_POLL_INTERVAL = 2000; // 2 seconds
const SLOW_POLL_INTERVAL = 5000; // 5 seconds
const VERY_SLOW_POLL_INTERVAL = 10000; // 10 seconds
const SLOW_POLL_THRESHOLD = 15; // polls (30 seconds at 2s interval)
const VERY_SLOW_POLL_THRESHOLD = 30; // polls (60 seconds)
const MAX_POLLING_ATTEMPTS = 60; // Max 2 minutes of polling

let pollingIntervalId = null;

// Determine polling interval based on attempts
const getPollingInterval = (attempts) => {
  if (attempts > VERY_SLOW_POLL_THRESHOLD) {
    return VERY_SLOW_POLL_INTERVAL;
  } else if (attempts > SLOW_POLL_THRESHOLD) {
    return SLOW_POLL_INTERVAL;
  }
  return INITIAL_POLL_INTERVAL;
};

// Polling middleware
export const createPollingMiddleware = () => {
  return (store) => (next) => (action) => {
    const result = next(action);
    const state = store.getState();
    const { reportId, isPolling, reportStatus, pollingAttempts } = state.aiReports;

    // Start polling when isPolling becomes true
    // Guard: only start if isPolling is actually true (synchronous backend sets it to false on fulfillment)
    if ((action.type === 'aiReports/startPolling' ||
        (action.type === 'aiReports/generate/fulfilled' && reportId)) && isPolling) {

      // Clear existing interval
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }

      // Start new polling interval
      const poll = async () => {
        const currentState = store.getState().aiReports;

        // Stop if no longer polling or exceeded max attempts
        if (!currentState.isPolling || currentState.pollingAttempts >= MAX_POLLING_ATTEMPTS) {
          clearInterval(pollingIntervalId);
          pollingIntervalId = null;
          store.dispatch(stopPolling());
          return;
        }

        // Increment attempts
        store.dispatch(incrementPollingAttempts());

        // Fetch report HTML (replaces old polling for status)
        await store.dispatch(fetchReportHTML(currentState.reportId));

        // Adjust interval based on attempts
        const newInterval = getPollingInterval(currentState.pollingAttempts);
        if (pollingIntervalId && newInterval !== getPollingInterval(currentState.pollingAttempts - 1)) {
          clearInterval(pollingIntervalId);
          pollingIntervalId = setInterval(poll, newInterval);
        }
      };

      // Initial poll
      poll();

      // Set up interval
      pollingIntervalId = setInterval(poll, INITIAL_POLL_INTERVAL);
    }

    // Stop polling when report completes or fails
    if (action.type === 'aiReports/stopPolling' ||
        action.type === 'aiReports/clearReport' ||
        (action.type === 'aiReports/pollStatus/fulfilled' &&
         ['completed', 'failed'].includes(action.payload.status))) {

      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
        pollingIntervalId = null;
      }
    }

    return result;
  };
};
